<div
  class="flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border border-[#EAEAEA] bg-white dark:border-line dark:bg-surface-1">
  <!-- hidden element: the custom transport below is the only control surface -->
  <audio #audioEl class="hidden" [src]="src()" preload="metadata" (loadedmetadata)="onMeta($event)"
    (durationchange)="onDuration($event)" (timeupdate)="onTime($event)" (play)="onPlay()" (pause)="onPause()"
    (ended)="onPause()" (error)="onError($event)"></audio>

  <!-- header: talk-time chips + debug toggle -->
  <div
    class="flex flex-wrap items-center gap-2 border-b border-[#EAEAEA] px-4 py-2.5 dark:border-line">
    <span class="text-xs font-semibold text-slate-700 dark:text-ink-soft">Transcript</span>
    <span class="text-[11px] text-slate-400 dark:text-ink-soft/50">{{ rows().length }} turns</span>
    @for (s of stats(); track s.name) {
    <span class="inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[10px] font-medium"
      [class]="s.ai ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300'
                    : 'bg-sky-50 text-sky-700 dark:bg-sky-500/10 dark:text-sky-300'">
      <span class="h-1.5 w-1.5 rounded-full" [class]="s.ai ? 'bg-emerald-500' : 'bg-sky-500'"></span>
      {{ s.name }} · {{ mmss(s.sec) }} · {{ s.pct }}%
    </span>
    }
    @if (deadAir() >= 1) {
    <span
      class="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-slate-500 dark:bg-white/10 dark:text-ink-soft/60">
      Dead air {{ mmss(deadAir()) }}</span>
    }
    <!-- debug mode is OFF by default (the normal view); switching it on adds the sync
         rows, gap cards and inspector, switching it off removes them again -->
    <button type="button" (click)="debug.set(!debug())" [attr.aria-pressed]="debug()"
      [title]="debug() ? 'Switch back to the normal view' : 'Show why speech did or did not reach the transcript'"
      class="ml-auto inline-flex items-center gap-1.5 rounded-md border px-2 py-0.5 text-[11px] font-medium transition-colors" [class]="debug()
        ? 'border-rose-300 bg-rose-50 text-rose-700 dark:border-rose-500/40 dark:bg-rose-500/10 dark:text-rose-300'
        : 'border-[#EAEAEA] text-slate-500 dark:border-line dark:text-ink-soft/60'">
      <span class="h-1.5 w-1.5 rounded-full" [class]="debug() ? 'bg-rose-500' : 'bg-slate-300 dark:bg-white/20'"></span>
      Debug mode · {{ debug() ? 'on' : 'off' }}@if (!debug() && dbg.findings().length) { · {{ dbg.findings().length }} }</button>
    @if (debug()) { <app-info-tip [text]="help.mode" side="left"></app-info-tip> }
  </div>

  <!-- turns (with gap cards merged in while debugging) + the inspector for the selected finding -->
  <div class="flex min-h-0 flex-1">
  <div class="scrollbar-theme min-h-0 flex-1 space-y-1 overflow-y-auto p-3">
    @for (item of dbg.flow(); track $index) {
    @if (item.kind === 'turn') {
    <!-- chat alignment: the bot (Ava/Alex/…) on the right, every human on the left -->
    <div class="flex" [class]="item.row.ai ? 'justify-end' : 'justify-start'">
      <div [id]="'pt-turn-' + item.index" (click)="seekRow(item.row)"
        class="flex max-w-[88%] gap-3 rounded-lg p-2.5 transition-all duration-300"
        [class]="rowClass(item.row, item.index)">
      <div class="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[10px] font-bold" [class]="item.row.ai
          ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300'
          : 'bg-sky-100 text-sky-700 dark:bg-sky-500/20 dark:text-sky-300'">
        @if (item.row.ai) {
        <app-icon-v2 name="bot" [size]="14"></app-icon-v2>
        } @else {
        {{ initials(item.row.name) }}
        }</div>
      <div class="min-w-0 flex-1">
        <div class="flex flex-wrap items-baseline gap-x-2">
          <span class="text-xs font-semibold"
            [class]="item.row.ai ? 'text-emerald-600 dark:text-emerald-400' : 'text-sky-600 dark:text-sky-400'">{{
            item.row.name }}</span>
          @if (debug() && item.row.start !== null && item.row.end !== null) {
          <span class="font-mono text-[10px] tabular-nums text-slate-400 dark:text-ink-soft/50">{{ mmss(item.row.start) }} – {{ mmss(item.row.end) }} · {{ item.row.dur }}</span>
          @if (dbg.badgeFor(item.row); as badge) {
          <span class="rounded px-1.5 py-px font-mono text-[9px] font-medium" [class]="badge.tone === 'ok'
              ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300'
              : 'bg-amber-50 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300'">{{ badge.text }}</span>
          }
          } @else {
          <span class="text-[10px] text-slate-400 dark:text-ink-soft/40">{{ item.row.ai ? 'AI agent' : 'Participant'
            }}</span>
          }
          <span class="ml-auto text-[10px] tabular-nums text-slate-400 dark:text-ink-soft/40">{{ item.row.offset }}</span>
        </div>
        <p class="text-sm leading-snug">
          <!-- word-by-word view: disabled via wordKaraoke (estimated times drift on real
               calls) — the branch is kept so flipping the flag restores it -->
          @if (wordKaraoke && activeIdx() === item.index && item.row.words) {
          <!-- karaoke: spoken bright, current word boxed, upcoming dimmed (times estimated).
               The separator MUST be an interpolation: a literal space between spans is a
               whitespace-only text node, which the template compiler strips — the words
               would render fused together. -->
          @for (w of item.row.words; track $index; let wi = $index) {
          <span [class]="wi < wordIdx() ? 'text-slate-700 dark:text-ink-soft'
              : wi === wordIdx() ? 'rounded bg-indigo-200/80 px-0.5 text-slate-900 dark:bg-indigo-400/40 dark:text-white'
              : 'text-slate-400 dark:text-ink-soft/40'">{{ w.w }}</span>{{ ' ' }}
          }
          } @else {
          <span class="text-slate-700 dark:text-ink-soft">{{ item.row.turn.text }}</span>
          }
        </p>
      </div>
      </div>
    </div>
    } @else {
    <!-- a finding, in the transcript flow at the moment it happened; click = inspect -->
    <div class="flex" [class]="item.finding.ai ? 'justify-end' : 'justify-start'">
      <div (click)="selectFinding(item.finding)"
        class="max-w-[88%] cursor-pointer rounded-lg border-2 border-dashed p-2.5 text-xs ring-rose-400/60 transition-shadow"
        [class]="findingClass(item.finding.kind)" [class.ring-2]="dbg.selected() === item.finding">
        <div class="flex flex-wrap items-baseline gap-x-2">
          <span class="font-semibold">{{ findingLabel(item.finding.kind) }}</span>
          <app-info-tip [text]="findingHelp[item.finding.kind]" side="right"></app-info-tip>
          <span class="font-mono text-[10px] opacity-80">{{ item.finding.who }} · {{ mmss(item.finding.t0) }} – {{ mmss(item.finding.t1) }} · {{ mmss(item.finding.t1 - item.finding.t0) }} s</span>
        </div>
        @if (item.finding.evidence.length) {
        <p class="mt-1 text-[12px]">{{ item.finding.evidence[0] }}</p>
        @if (item.finding.evidence.length > 1) {
        <p class="mt-0.5 font-mono text-[10px] opacity-75">{{ item.finding.evidence.slice(1).join(' · ') }}</p>
        }
        }
        <div class="mt-2 flex flex-wrap gap-1.5" (click)="$event.stopPropagation()">
          <button type="button" (click)="playFinding(item.finding, item.finding.ai ? 'agent' : 'human')"
            class="rounded-md bg-indigo-600 px-2 py-0.5 text-[11px] font-medium text-white hover:bg-indigo-500">▶ Play this span ({{ item.finding.ai ? 'AI' : 'Human' }} only)</button>
          <app-info-tip [text]="help.playSolo" side="right"></app-info-tip>
          <button type="button" (click)="playFinding(item.finding, 'mixed')"
            class="rounded-md border border-current/40 px-2 py-0.5 text-[11px] font-medium">Play with {{ item.finding.ai ? 'human' : 'AI' }}</button>
          <app-info-tip [text]="help.playBoth"></app-info-tip>
          <button type="button" (click)="dbg.loopSpan.set(!dbg.loopSpan())" [attr.aria-pressed]="dbg.loopSpan()"
            class="rounded-md border border-current/40 px-2 py-0.5 text-[11px] font-medium" [class.font-bold]="dbg.loopSpan()">Loop{{ dbg.loopSpan() ? ' ✓' : '' }}</button>
          <app-info-tip [text]="help.loop"></app-info-tip>
          <button type="button" (click)="copyReport(item.finding)"
            class="rounded-md border border-current/40 px-2 py-0.5 text-[11px] font-medium">Copy report line</button>
        </div>
      </div>
    </div>
    }
    } @empty {
    <p class="py-8 text-center text-sm text-slate-400">No transcript was captured for this session.</p>
    }
  </div>
  @if (debug() && dbg.inspection(); as insp) {
  <!-- inspector: every signal we hold for the selected span, and a verdict -->
  <aside class="scrollbar-theme w-72 shrink-0 overflow-y-auto border-l border-[#EAEAEA] p-3 text-[11px] dark:border-line">
    <div class="mb-2 flex items-center justify-between">
      <span class="inline-flex items-center gap-1.5 font-mono text-[10px] font-semibold uppercase tracking-wide text-slate-500 dark:text-ink-soft/70">{{ insp.title }}
        <app-info-tip [text]="help.inspector" side="right"></app-info-tip></span>
      <button type="button" (click)="dbg.selected.set(null)" aria-label="Close inspector" class="text-slate-400 hover:text-slate-600 dark:hover:text-white">✕</button>
    </div>
    <dl class="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1.5">
      @for (r of insp.rows; track r.label) {
      <dt class="inline-flex items-center gap-1 text-slate-400 dark:text-ink-soft/50">{{ r.label }}
        @if (help.inspectorRows[r.label]) { <app-info-tip [text]="help.inspectorRows[r.label]" side="right"></app-info-tip> }</dt>
      <dd class="m-0" [class]="r.tone === 'bad' ? 'text-rose-600 dark:text-rose-300'
          : r.tone === 'ok' ? 'text-emerald-600 dark:text-emerald-300'
          : r.tone === 'dim' ? 'text-slate-400 dark:text-ink-soft/40' : 'text-slate-700 dark:text-ink-soft'">{{ r.value }}</dd>
      }
      <dt class="inline-flex items-center gap-1 text-slate-400 dark:text-ink-soft/50">Verdict
        <app-info-tip [text]="help.inspectorRows['Verdict']" side="right"></app-info-tip></dt>
      <dd class="m-0 font-medium" [class]="insp.bad ? 'text-rose-600 dark:text-rose-300' : 'text-amber-600 dark:text-amber-300'">{{ insp.verdict }}</dd>
    </dl>
  </aside>
  }
  </div>

  <!-- dock: findings strip + speaker rows + transport -->
  <div class="border-t border-[#EAEAEA] px-4 py-2.5 dark:border-line">
    @if (debug()) {
    <div class="mb-2 flex flex-wrap items-center gap-2">
      @for (k of dbg.kinds; track k) {
      @if (dbg.countOf(k)) {
      <span class="inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[10px] font-medium" [class]="findingClass(k)">
        <span class="h-1.5 w-1.5 rounded-full bg-current"></span>{{ dbg.countOf(k) }} {{ findingLabel(k).toLowerCase() }}
        <app-info-tip [text]="findingHelp[k]" side="right"></app-info-tip></span>
      }
      }
      @if (!dbg.findings().length) {
      <span class="text-[10px] text-slate-400 dark:text-ink-soft/50">No sync findings</span>
      }
      @if (dbg.gateClosedSec() >= 1) {
      <span class="inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-slate-500 dark:bg-white/10 dark:text-ink-soft/60">gate closed {{ mmss(dbg.gateClosedSec()) }} of AI time
        <app-info-tip [text]="help.gateClosed"></app-info-tip></span>
      }
      <span class="ml-auto inline-flex items-center gap-2">
        @if (!dbg.hasWaveform()) {
        <button type="button" (click)="buildWaveform.emit()"
          class="rounded-md border border-indigo-300 bg-indigo-50 px-2 py-0.5 text-[10px] font-medium text-indigo-700 hover:bg-indigo-100 dark:border-indigo-500/40 dark:bg-indigo-500/10 dark:text-indigo-300 dark:hover:bg-white/10">Build waveform</button>
        <app-info-tip [text]="help.buildWaveform" side="left"></app-info-tip>
        }
        <button type="button" (click)="copyReport()" [disabled]="!dbg.findings().length"
          class="rounded-md border border-[#EAEAEA] px-2 py-0.5 text-[10px] font-medium text-slate-600 hover:bg-gray-50 disabled:opacity-40 dark:border-line dark:text-ink-soft/70 dark:hover:bg-white/5">Copy report</button>
        <app-info-tip [text]="help.copyReport" side="left"></app-info-tip>
        <span class="inline-flex items-center gap-1.5 rounded-full bg-indigo-50 px-2 py-0.5 text-[10px] font-medium text-indigo-700 dark:bg-indigo-500/15 dark:text-indigo-300">Debug mode · on
          <app-info-tip [text]="help.mode" side="left"></app-info-tip></span>
      </span>
    </div>
    }
    @if (lanes().length) {
    <!-- speaker legend + who is speaking at the playhead (name and the span being spoken) -->
    <div class="mb-1.5 flex flex-wrap items-center gap-x-3 gap-y-1">
      @for (lane of lanes(); track lane.name) {
      <span class="inline-flex items-center gap-1.5 text-[10px] font-medium text-slate-500 dark:text-ink-soft/60">
        <span class="h-1.5 w-1.5 shrink-0 rounded-full" [class]="laneDotClass(lane)"></span>
        {{ lane.name }}
      </span>
      }
      @if (debug()) { <span class="text-slate-400 dark:text-ink-soft/50"><app-info-tip [text]="help.rows" side="right"></app-info-tip></span> }
      <span class="ml-auto inline-flex items-center gap-1 text-[10px]">
        <span class="mr-1 font-semibold uppercase tracking-wide text-slate-400 dark:text-ink-soft/40">Zoom</span>
        <button type="button" (click)="zoomer.out()" [disabled]="zoomer.level() === 1" title="Zoom out"
          class="rounded-md border border-[#EAEAEA] px-1.5 py-0.5 text-[11px] font-medium leading-none text-slate-600 hover:bg-gray-50 disabled:opacity-40 dark:border-line dark:text-ink-soft/70 dark:hover:bg-white/5">−</button>
        <span class="w-7 text-center font-mono tabular-nums text-slate-500 dark:text-ink-soft/70">{{ zoomer.level() }}×</span>
        <button type="button" (click)="zoomer.in()" [disabled]="zoomer.level() === 16" title="Zoom in: the tracks widen and scroll sideways; the playhead stays in view"
          class="rounded-md border border-[#EAEAEA] px-1.5 py-0.5 text-[11px] font-medium leading-none text-slate-600 hover:bg-gray-50 disabled:opacity-40 dark:border-line dark:text-ink-soft/70 dark:hover:bg-white/5">+</button>
        @if (zoomer.level() > 1) {
        <button type="button" (click)="zoomer.fit()" title="Fit the whole call"
          class="rounded-md border border-[#EAEAEA] px-1.5 py-0.5 text-[10px] font-medium leading-none text-slate-600 hover:bg-gray-50 dark:border-line dark:text-ink-soft/70 dark:hover:bg-white/5">Fit</button>
        }
      </span>
      <span class="inline-flex items-center gap-2 text-[10px] tabular-nums">
        @if (agentNow(); as now) {
        <span class="rounded-full bg-emerald-50 px-2 py-0.5 font-medium text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300">
          {{ now.name }} · {{ mmss(now.at) }}–{{ mmss(now.end) }}</span>
        }
        @if (humanNow(); as now) {
        <span class="rounded-full bg-sky-50 px-2 py-0.5 font-medium text-sky-700 dark:bg-sky-500/10 dark:text-sky-300">
          {{ now.name }} · {{ mmss(now.at) }}–{{ mmss(now.end) }}</span>
        }
        @if (!agentNow() && !humanNow()) {
        <span class="text-slate-300 dark:text-ink-soft/25">silence</span>
        }
      </span>
    </div>
    <!-- editor-style rows. Two columns: the labels (listen toggles) stay put on the left;
         every track on the right sits in ONE horizontal scroller, `zoom × 100%` wide, so the
         AI / Human / Transcript / Events rows and the ruler zoom and scroll together. -->
    <div class="flex items-stretch gap-2">
      <div class="w-20 shrink-0 space-y-1">
        @for (row of [{ label: 'AI', ai: true, side: 'agent' }, { label: 'Human', ai: false, side: 'human' }]; track row.label) {
        <div class="flex items-center justify-end" [class]="dbg.waveRows() ? 'h-7' : 'h-2.5'">
          <button type="button" (click)="toggleSide(row.side)" [attr.aria-pressed]="!rowMuted(row.ai)"
            [disabled]="!canToggle(row.side)" [title]="row.label + ' audio on/off'"
            class="inline-flex w-20 items-center justify-end gap-1.5 rounded-md border px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide transition-colors disabled:cursor-not-allowed"
            [class]="rowMuted(row.ai)
              ? 'border-transparent text-slate-400 line-through dark:text-ink-soft/40'
              : row.ai
                ? 'border-emerald-300 bg-emerald-50 text-emerald-700 dark:border-emerald-500/40 dark:bg-emerald-500/10 dark:text-emerald-300'
                : 'border-sky-300 bg-sky-50 text-sky-700 dark:border-sky-500/40 dark:bg-sky-500/10 dark:text-sky-300'">
            <span class="h-1.5 w-1.5 rounded-full" [class]="rowMuted(row.ai) ? 'bg-slate-300 dark:bg-white/20' : row.ai ? 'bg-emerald-500' : 'bg-sky-500'"></span>{{ row.label }}</button>
        </div>
        }
        @if (debug()) {
        <div class="flex h-3.5 items-center justify-end">
          <span class="inline-flex items-center gap-1 font-mono text-[9px] font-semibold uppercase tracking-wide text-slate-400 dark:text-ink-soft/40">Transcript
            <app-info-tip [text]="help.transcriptRow" side="right"></app-info-tip></span>
        </div>
        <div class="flex h-7 items-center justify-end">
          <span class="inline-flex items-center gap-1 font-mono text-[9px] font-semibold uppercase tracking-wide text-slate-400 dark:text-ink-soft/40">Events
            <app-info-tip [text]="help.eventsRow" side="right"></app-info-tip></span>
        </div>
        }
        <div class="h-3.5"></div>
      </div>
      <div #scroller class="scrollbar-theme min-w-0 flex-1 overflow-x-auto overflow-y-hidden">
      <div class="space-y-1" [style.width.%]="zoomer.level() * 100">
        @for (row of [{ ai: true, lanes: agentLanes(), env: dbg.envAgent(), color: '#2FC98E' }, { ai: false, lanes: humanLanes(), env: dbg.envHuman(), color: '#4FA8F5' }]; track row.ai) {
        <div class="relative cursor-pointer rounded bg-gray-100 dark:bg-black/30"
          [class]="dbg.waveRows() ? 'h-7' : 'h-2.5'" [class.opacity-40]="rowMuted(row.ai)" (click)="seekLane($event)">
          @if (row.env.length) {
          <!-- the recorded leg's loudness: "never arrived" vs "arrived, not transcribed" -->
          <app-envelope-track [levels]="row.env" [color]="row.color" [zoom]="zoomer.level()"></app-envelope-track>
          }
          @if (debug() && !row.ai) {
          <!-- echo gate closed: quiet mic chunks were replaced by silence (AI audible) -->
          @for (g of dbg.gateBlocks(); track $index) {
          <div class="absolute inset-y-0" [style.left.%]="g.leftPct" [style.width.%]="g.widthPct"
            style="background: repeating-linear-gradient(135deg, rgba(240,96,92,.45) 0 4px, transparent 4px 8px)"
            [title]="'echo gate closed ' + mmss(g.m.t) + ' – ' + mmss(g.m.t1!) + ' (AI audible)'"></div>
          }
          }
          @for (lane of row.lanes; track lane.name) {
          @for (seg of lane.segs; track $index) {
          @if (debug() && dbg.flagAt(row.ai, seg.at, seg.end); as flag) {
          <div class="absolute rounded outline outline-2 outline-offset-1" [class]="laneBarClass(lane)"
            [class.outline-amber-400]="flag.kind === 'talked_over'" [class.outline-rose-400]="flag.kind !== 'talked_over'"
            [style.top.px]="dbg.waveRows() ? 8 : 0" [style.bottom.px]="dbg.waveRows() ? 8 : 0" [style.opacity]="row.env.length ? 0.7 : 1"
            [style.left.%]="seg.leftPct" [style.width.%]="seg.widthPct"
            [title]="segTitle(lane, seg) + ' · ' + findingLabel(flag.kind)"></div>
          } @else {
          <div class="absolute rounded-full" [class]="laneBarClass(lane)"
            [style.top.px]="dbg.waveRows() ? 8 : 0" [style.bottom.px]="dbg.waveRows() ? 8 : 0" [style.opacity]="row.env.length ? 0.7 : 1"
            [style.left.%]="seg.leftPct" [style.width.%]="seg.widthPct" [title]="segTitle(lane, seg)"></div>
          }
          }
          }
          <div class="pointer-events-none absolute inset-y-0 w-px bg-slate-500 dark:bg-white/70"
            [style.left.%]="cursorPct()"></div>
        </div>
        }
        @if (debug()) {
        <!-- what was WRITTEN: turn windows (solid = matched to speech, dashed = estimated);
             findings drawn over them where audio and text disagree -->
        <div class="relative h-3.5 rounded bg-gray-100 dark:bg-black/30" (click)="seekLane($event)">
          @for (b of dbg.turnBlocks(); track $index) {
          <div class="absolute inset-y-0.5 rounded-sm border" [class]="b.row.ai
              ? 'border-emerald-400 dark:border-emerald-500/80' : 'border-sky-400 dark:border-sky-500/80'"
            [class.border-dashed]="!b.row.measured" [style.left.%]="b.leftPct" [style.width.%]="b.widthPct"
            [title]="b.row.name + ' · ' + b.row.offset + '–' + mmss(b.row.end!) + (b.row.measured ? '' : ' (estimated)')"></div>
          }
          @for (b of dbg.findingBlocks(); track $index) {
          <div class="absolute inset-y-0 cursor-pointer rounded-sm border-2 border-dashed" [class]="findingClass(b.f.kind)"
            (click)="selectFinding(b.f); $event.stopPropagation()"
            [style.left.%]="b.leftPct" [style.width.%]="b.widthPct"
            [title]="findingLabel(b.f.kind) + ' · ' + b.f.who + ' · ' + mmss(b.f.t0) + '–' + mmss(b.f.t1)"></div>
          }
          <div class="pointer-events-none absolute inset-y-0 w-px bg-slate-500 dark:bg-white/70" [style.left.%]="cursorPct()"></div>
        </div>
        <!-- what the call DECIDED: host marks (gate closed, STT audio sent, guest audio
             arriving, interrupt outcomes) on the same clock, labelled where there is room -->
        <div class="relative h-7 rounded bg-gray-100 dark:bg-black/30" (click)="seekLane($event)">
          @for (b of dbg.markBlocks(); track $index) {
          @if (b.widthPct > 0) {
          <div class="absolute bottom-1 h-2 rounded-sm" [class]="markClass(b.m)"
            [style.left.%]="b.leftPct" [style.width.%]="b.widthPct" [title]="markTitle(b.m)"></div>
          } @else {
          <div class="absolute bottom-0 h-3 w-0.5" [class]="markClass(b.m)" [style.left.%]="b.leftPct" [title]="markTitle(b.m)"></div>
          @if (b.label) {
          <span class="pointer-events-none absolute top-0 translate-x-1 whitespace-nowrap font-mono text-[8px] leading-none text-slate-500 dark:text-ink-soft/60"
            [style.left.%]="b.leftPct">{{ markLabel(b.m) }}</span>
          }
          }
          }
          @if (!dbg.markBlocks().length) {
          <span class="absolute inset-y-0 left-2 flex items-center text-[9px] text-slate-400 dark:text-ink-soft/40">no marks for this session (recorded before the debug marks shipped)</span>
          }
          <div class="pointer-events-none absolute inset-y-0 w-px bg-slate-500 dark:bg-white/70" [style.left.%]="cursorPct()"></div>
        </div>
        }
        <!-- time ruler, finer when zoomed -->
        <div class="relative h-3.5">
          @for (t of ticks(); track t.sec) {
          <span class="absolute top-0 -translate-x-1/2 text-[9px] tabular-nums leading-none text-slate-400 dark:text-ink-soft/40"
            [style.left.%]="t.pct">
            <span class="mx-auto mb-0.5 block h-1 w-px bg-slate-300 dark:bg-white/20"></span>{{ mmss(t.sec) }}</span>
          }
        </div>
      </div>
      </div>
    </div>
    }
    <div class="mt-1.5 flex items-center gap-2">
      <button type="button" (click)="skip(-10)" aria-label="Back 10 seconds"
        class="inline-flex items-center gap-1 rounded-lg border border-[#EAEAEA] px-2.5 py-1 text-xs font-medium text-slate-600 hover:bg-gray-50 dark:border-line dark:text-ink-soft dark:hover:bg-white/5">
        <app-icon-v2 name="rotate-ccw" [size]="12"></app-icon-v2>
        10</button>
      <button type="button" (click)="toggle()" [attr.aria-label]="playing() ? 'Pause' : 'Play'"
        class="flex h-7 w-9 shrink-0 items-center justify-center rounded-lg bg-indigo-600 text-white hover:bg-indigo-500">
        <app-icon-v2 [name]="playing() ? 'pause' : 'play'" [size]="14" [class.ml-0.5]="!playing()"></app-icon-v2>
      </button>
      <button type="button" (click)="skip(10)" aria-label="Forward 10 seconds"
        class="inline-flex items-center gap-1 rounded-lg border border-[#EAEAEA] px-2.5 py-1 text-xs font-medium text-slate-600 hover:bg-gray-50 dark:border-line dark:text-ink-soft dark:hover:bg-white/5">
        10
        <app-icon-v2 name="rotate-cw" [size]="12"></app-icon-v2>
      </button>
      <span class="ml-2 text-[10px] font-semibold uppercase tracking-wide text-slate-400 dark:text-ink-soft/40">Speed</span>
      @for (s of speeds; track s) {
      <button type="button" (click)="setRate(s)"
        class="rounded-md border px-2 py-0.5 text-[11px] font-medium transition-colors" [class]="rate() === s
          ? 'border-indigo-300 bg-indigo-50 text-indigo-700 dark:border-indigo-500/40 dark:bg-indigo-500/10 dark:text-indigo-300'
          : 'border-[#EAEAEA] text-slate-500 dark:border-line dark:text-ink-soft/60'">{{ s }}×</button>
      }
      <span class="ml-auto text-xs tabular-nums text-slate-500 dark:text-ink-soft/70">
        {{ mmss(playhead()) }} / {{ mmss(durationSec()) }}</span>
    </div>
  </div>
</div>
